import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { partOrderService } from '../../services/api';

const SupplyChart = () => {
	const [requirementsData, setRequirementsData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);
				const data = await partOrderService.getSupplyOrder();

				// Get the top 10 items by total quantity needed
				const topItems = [...data]
					.sort((a, b) => b.total_quantity_needed - a.total_quantity_needed)
					.slice(0, 10)
					.map(item => ({
						name: item.part_type_name,
						required: parseFloat(item.total_quantity_needed).toFixed(2),
						toOrder: item.units_to_order,
						cost: parseFloat(item.estimated_cost).toFixed(2)
					}));

				setRequirementsData(topItems);
				setError(null);
			} catch (err) {
				console.error("Failed to fetch requirements data:", err);
				setError("Failed to load part requirements data");
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, []);

	if (loading) {
		return (
			<div className="flex justify-center items-center h-80">
				<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="p-4 text-red-500 border border-red-200 rounded-md">
				<p className="font-semibold">Error loading chart data</p>
				<p>{error}</p>
			</div>
		);
	}

	return (
		<div className="bg-white p-6 rounded-lg shadow-md">
			<h2 className="text-xl font-bold mb-4">Top Part Requirements</h2>
			<div className="h-80">
				<ResponsiveContainer width="100%" height="100%">
					<BarChart
						data={requirementsData}
						margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
					>
						<CartesianGrid strokeDasharray="3 3" />
						<XAxis
							dataKey="name"
							angle={-45}
							textAnchor="end"
							height={70}
							tick={{ fontSize: 12 }}
						/>
						<YAxis />
						<Tooltip
							formatter={(value, name) => {
								if (name === "cost") return [`$${value}`, "Est. Cost"];
								if (name === "required") return [value, "Quantity Needed"];
								if (name === "toOrder") return [value, "Units to Order"];
								return [value, name];
							}}
						/>
						<Legend />
						<Bar dataKey="required" fill="#8884d8" name="Quantity Needed" />
						<Bar dataKey="toOrder" fill="#82ca9d" name="Units to Order" />
					</BarChart>
				</ResponsiveContainer>
			</div>
			<div className="mt-4">
				<p className="text-sm text-gray-500">
					This chart shows the top 10 part types based on current requirements.
				</p>
			</div>
		</div>
	);
};

export default SupplyChart;
